import {
  modeOklch,
  modeRgb,
  modeLrgb,
  converter,
  useMode as culoriUseMode,
  serializeHex,
  wcagContrast
} from 'culori/fn'

function clampChroma(c) {
  return Math.max(0, Math.min(0.4, c))
}

export function generatePaletteCulori({ backgroundColor, textColor, buttonColor }) {
  const oklch = culoriUseMode(modeOklch)
  culoriUseMode(modeRgb)
  culoriUseMode(modeLrgb)

  const rgbConverter = converter('rgb')

  // COLOR TEXT

  const colorTextStandard = oklch(textColor)

  const colorTextSecondary = oklch(textColor)
  colorTextSecondary.l += colorTextSecondary.l > 0.5 ? -0.25 : 0.25

  const colorTextDisabled = oklch(colorTextSecondary)
  colorTextDisabled.c = clampChroma(colorTextDisabled.c - 0.2)
  colorTextDisabled.l += colorTextDisabled.l > 0.5 ? -0.1 : 0.2

  // COLOR BACKGROUND

  const colorBackgroundStandard = oklch(backgroundColor)

  const colorBackgroundSecondary = oklch(backgroundColor)
  colorBackgroundSecondary.l += colorBackgroundSecondary.l < 0.75 ? 0.075 : -0.075

  const colorBackgroundTertiary = oklch(backgroundColor)
  colorBackgroundTertiary.l += colorBackgroundTertiary.l < 0.75 ? 0.125 : -0.125

  // COLOR BACKGROUND INVERSE

  const colorBackgroundInverseStandard = oklch(backgroundColor)
  colorBackgroundInverseStandard.c = colorBackgroundInverseStandard.l >= 0.2 ? 0.01 : 0.4
  colorBackgroundInverseStandard.l = colorBackgroundInverseStandard.l < 0.5
    ? 1 - (colorBackgroundInverseStandard.l / 1000)
    : colorBackgroundInverseStandard.l / 1000

  const colorBackgroundInverseSecondary = oklch(colorBackgroundInverseStandard)
  colorBackgroundInverseSecondary.l = colorBackgroundInverseSecondary.l < 0.5 ? 0.3 : -0.1

  const colorBackgroundInverseTertiary = oklch(colorBackgroundInverseStandard)
  colorBackgroundInverseTertiary.l += colorBackgroundInverseTertiary.l < 0.5 ? 0.4 : -0.2

  // COLOR TEXT INVERSE

  const colorTextInverseStandard = oklch('#ffffff')
  if (wcagContrast(colorTextInverseStandard, buttonColor) < 4.5) {
    colorTextInverseStandard.l = 0
  }

  const colorTextInverseSecondary = oklch(colorTextInverseStandard)
  colorTextInverseSecondary.l += colorTextInverseSecondary.l <= 0.9 ? -0.15 : 0.15
  colorTextInverseSecondary.l += colorBackgroundInverseSecondary.l > 0.55 ? 0.2 : -0.1

  const colorTextInverseDisabled = oklch(colorTextInverseSecondary)
  colorTextInverseDisabled.c = clampChroma(colorTextInverseDisabled.c - 0.3)
  colorTextInverseDisabled.l += colorBackgroundTertiary.l > 0.6 ? -0.1 : 0.1

  const colorTextInverseAction = oklch('#ffffff')
  colorTextInverseAction.l = oklch(buttonColor).l >= 0.6 ? 0.1 : 0.9

  const colorTextActionContrast = oklch(buttonColor)
  colorTextActionContrast.l += colorTextActionContrast.l > 0.5 ? -0.1 : 0.1

  // COLOR SURFACE ACTION
  const colorSurfaceAction = oklch(buttonColor)

  const colorSurfaceActionHover = oklch(buttonColor)
  colorSurfaceActionHover.l -= 0.05

  const colorSurfaceActionActive = oklch(buttonColor)
  colorSurfaceActionActive.l -= 0.1

  const colorSurfaceActionSecondary = oklch(buttonColor)
  colorSurfaceActionSecondary.l += colorSurfaceActionSecondary.l > 0.5 ? -0.1 : 0.1

  const colorSurfaceActionSecondaryHover = oklch(buttonColor)
  colorSurfaceActionSecondaryHover.l += colorSurfaceActionSecondaryHover.l < 0.7 ? 0.2 : -0.2

  const colorSurfaceActionSecondaryActive = oklch(buttonColor)
  colorSurfaceActionSecondaryActive.l += colorSurfaceActionSecondaryActive.l > 0.5 ? -0.45 : 0.45

  // COLOR ICON
  const colorIconHover = oklch(colorTextStandard)
  colorIconHover.l += colorIconHover.l > 0.5 ? -0.1 : 0.1

  return {
    colorTextStandard: serializeHex(rgbConverter(colorTextStandard)),
    colorTextSecondary: serializeHex(rgbConverter(colorTextSecondary)),
    colorTextDisabled: serializeHex(rgbConverter(colorTextDisabled)),
    colorTextAction: buttonColor,
    colorTextActionContrast: serializeHex(rgbConverter(colorTextActionContrast)),
    colorTextInverseStandard: serializeHex(rgbConverter(colorTextInverseStandard)),
    colorTextInverseSecondary: serializeHex(rgbConverter(colorTextInverseSecondary)),
    colorTextInverseDisabled: serializeHex(rgbConverter(colorTextInverseDisabled)),
    colorTextInverseAction: serializeHex(rgbConverter(colorTextInverseAction)),
    colorTextInverseActionContrast: '',

    colorBackgroundInverseStandard: serializeHex(rgbConverter(colorBackgroundInverseStandard)),
    colorBackgroundInverseSecondary: serializeHex(rgbConverter(colorBackgroundInverseSecondary)),
    colorBackgroundInverseTertiary: serializeHex(rgbConverter(colorBackgroundInverseTertiary)),

    colorBackgroundStandard: serializeHex(rgbConverter(colorBackgroundStandard)),
    colorBackgroundSecondary: serializeHex(rgbConverter(colorBackgroundSecondary)),
    colorBackgroundTertiary: serializeHex(rgbConverter(colorBackgroundTertiary)),

    colorSurfaceAction: serializeHex(rgbConverter(colorSurfaceAction)),
    colorSurfaceActionHover: serializeHex(rgbConverter(colorSurfaceActionHover)),
    colorSurfaceActionActive: serializeHex(rgbConverter(colorSurfaceActionActive)),
    colorSurfaceActionSecondary: serializeHex(rgbConverter(colorSurfaceActionSecondary)),
    colorSurfaceActionSecondaryHover: serializeHex(rgbConverter(colorSurfaceActionSecondaryHover)),
    colorSurfaceActionSecondaryActive: serializeHex(rgbConverter(colorSurfaceActionSecondaryActive)),

    colorSurfaceInverseStandard: backgroundColor,
    colorSurfaceInverseSecondary: serializeHex(rgbConverter(colorBackgroundSecondary)),
    colorSurfaceInverseDisabled: serializeHex(rgbConverter(colorBackgroundTertiary)),

    colorStrokeFocus: buttonColor,

    colorIconStandard: serializeHex(rgbConverter(colorTextStandard)),
    colorIconHover: serializeHex(rgbConverter(colorIconHover)),
    colorIconSecondary: serializeHex(rgbConverter(colorTextSecondary)),
    colorIconActive: buttonColor,
    colorIconDisabled: serializeHex(rgbConverter(colorTextDisabled)),
    colorIconInverseStandard: serializeHex(rgbConverter(colorTextInverseStandard)),
    colorIconInverseHover: '',
    colorIconInverseSecondary: serializeHex(rgbConverter(colorTextInverseSecondary)),
    colorIconInverseActive: serializeHex(rgbConverter(colorTextInverseAction)),
    colorIconInverseDisabled: serializeHex(rgbConverter(colorTextInverseDisabled))
  }
}
